import { useState } from "react";
import { GOLD, NAVY, WHITE } from "../styles/palette";
import { InvoicePreviewModal } from "./InvoicePreviewModal";
import { InvoiceEditModal } from "./InvoiceEditModal";
import { ACTIVE_THEME } from "../themes";
import { useBackClose } from "../lib/navStack";
import { useIsMobile } from "../lib/useIsMobile";

export function Invoices({ invoices, onChargeAll, onApprove, onDismiss, onEdit, onRetryCharge, onIssueRefund, onVoidInvoice }) {
  const isMobile = useIsMobile();
  const [filter,        setFilter]        = useState("all");
  const [editInvoice,   setEditInvoice]   = useState(null);
  const [search,        setSearch]        = useState("");
  const [invoiceModal,  setInvoiceModal]  = useState(null); // invoice to preview/send
  const [hoveredId,     setHoveredId]     = useState(null); // apple-native: row hover for action reveal
  // Browser Back closes an open invoice modal before it leaves the tab (see navStack).
  useBackClose(!!editInvoice, () => setEditInvoice(null));
  useBackClose(!!invoiceModal, () => setInvoiceModal(null));

  const needsApproval = invoices.filter(i=>i.status==="draft");
  const filtered      = invoices.filter(i=>{
    const matchTab    = filter==="all" ? i.status!=="void" : i.status===filter;
    const q           = search.toLowerCase().trim();
    const matchSearch = !q || (i.client_name||"").toLowerCase().includes(q) || (i.invoice_number||"").toLowerCase().includes(q) || String(i.amount).includes(q);
    return matchTab && matchSearch;
  });
  const total         = filtered.reduce((s,i)=>s+(Number(i.amount)||0),0);

  const fmtAmt = (a) => Number(a).toFixed(2);
  // Phone: the btn-sm actions are 11px/~24px targets and 'Charge Now' / 'Void' / 'Refund'
  // sit 6px apart — grow them to a real 36px+ tap target and let them share the row width.
  const actBtn = (extra) => isMobile
    ? { fontSize: 13, padding: "9px 14px", minHeight: 36, flex: "1 1 auto", justifyContent: "center", ...extra }
    : { fontSize: 11, ...extra };

  return (
    <div style={ACTIVE_THEME === "apple-native" ? { maxWidth: 1280, margin: "0 auto" } : undefined}>
      {/* ── Draft / Needs Approval Banner ── */}
      {needsApproval.length > 0 && (
        <div style={{background:"#fffbeb",border:"2px solid "+GOLD,borderRadius:12,padding:20,marginBottom:20}}>
          <div style={{fontFamily:"Syne",fontWeight:700,fontSize:15,color:NAVY,marginBottom:12}}>
            ⚠️ {needsApproval.length} Draft Invoice{needsApproval.length>1?"s":""} Awaiting Your Review
          </div>
          {needsApproval.map(inv=>(
            <div key={inv.id} style={{background:WHITE,border:"1px solid #fde047",borderRadius:10,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:NAVY}}>{inv.client_name}</div>
                  <div style={{fontSize:12,color:"#8892b0",marginTop:3}}>{inv.invoice_number || inv.date}</div>
                  {inv.notes&&<div style={{fontSize:12,color:"#854d0e",background:"#fef9c3",padding:"3px 8px",borderRadius:4,marginTop:6,display:"inline-block"}}>{inv.notes}</div>}
                </div>
                {/* Phone: amount on its own line, then a full-width wrapping button row so the
                    money-moving CTA and Void are each a full-height tap target. */}
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",width:isMobile?"100%":undefined}}>
                  <div style={{fontFamily:"Syne",fontWeight:800,fontSize:24,color:NAVY,flexBasis:isMobile?"100%":undefined}}>${fmtAmt(inv.amount)}</div>
                  {inv.stripe_hosted_url&&(
                    <a href={inv.stripe_hosted_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={isMobile?actBtn():undefined}>👁 View</a>
                  )}
                  <button className="btn btn-ghost btn-sm" style={isMobile?actBtn():undefined} onClick={()=>setEditInvoice(inv)}>✏️ Edit</button>
                  {inv.status==="draft" && (
                    inv.stripe_invoice_id
                      ? <button className="btn btn-gold" style={isMobile?{flex:"1 1 100%",justifyContent:"center",minHeight:44}:undefined} onClick={()=>onApprove(inv)}>⚡ Finalize & Charge</button>
                      : <button className="btn btn-gold" style={isMobile?{flex:"1 1 100%",justifyContent:"center",minHeight:44}:undefined} onClick={()=>onApprove(inv)}>✓ Approve & Charge</button>
                  )}
                  {inv.status==="open" && inv.stripe_invoice_id && (
                    <button className="btn btn-gold" style={isMobile?{flex:"1 1 100%",justifyContent:"center",minHeight:44}:undefined} onClick={()=>onApprove(inv)}>⚡ Charge Now</button>
                  )}
                  <button className="btn btn-sm" style={actBtn({background:"#f4f5f7",color:"#42526e",border:"none"})} onClick={()=>onVoidInvoice&&onVoidInvoice(inv)}>⊘ Void</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Stats ── */}
      <div className="stats-row" style={{gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))"}}>
        {[
          {label:"Total Billed",  value:`$${invoices.filter(i=>i.status!=="draft"&&i.status!=="void").reduce((s,i)=>s+(Number(i.amount)||0),0).toFixed(2)}`, sub:`${invoices.filter(i=>i.status!=="void").length} invoices`, alert:false},
          {label:"Collected",     value:`$${invoices.filter(i=>i.status==="paid").reduce((s,i)=>s+(Number(i.amount)||0),0).toFixed(2)}`, sub:`${invoices.filter(i=>i.status==="paid").length} paid`, alert:false},
          {label:"Outstanding",   value:`$${invoices.filter(i=>i.status==="open").reduce((s,i)=>s+(Number(i.amount)||0),0).toFixed(2)}`, sub:`${invoices.filter(i=>i.status==="open").length} open`, alert:false},
          {label:"Uncollectible", value:`$${invoices.filter(i=>i.status==="uncollectible").reduce((s,i)=>s+(Number(i.amount)||0),0).toFixed(2)}`, sub:`${invoices.filter(i=>i.status==="uncollectible").length} failed`, alert:invoices.filter(i=>i.status==="uncollectible").length>0},
        ].map(s=>(
          <div key={s.label} className="stat-card" style={s.alert?{borderLeft:"4px solid #de350b"}:{}}>
            <div className="stat-accent" style={s.alert?{background:"#de350b"}:{}}/>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={s.alert?{color:"#de350b"}:{}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="card">
        {/* Search bar */}
        <div style={{marginBottom:12}}>
          <input
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="🔍  Search by client name, invoice #, or amount..."
            style={{width:"100%"}}
          />
        </div>
        <div className="flex justify-between items-center" style={{marginBottom:16,flexWrap:"wrap",gap:12}}>
          <div className="tabs" style={{marginBottom:0,flexWrap:"wrap"}}>
            {[
              {val:"all",           label:"All"},
              {val:"draft",          label:"Draft",         count:invoices.filter(i=>i.status==="draft").length},
              {val:"open",           label:"Open",          count:invoices.filter(i=>i.status==="open").length},
              {val:"paid",           label:"Paid",          count:invoices.filter(i=>i.status==="paid").length},
              {val:"uncollectible",  label:"Uncollectible", count:invoices.filter(i=>i.status==="uncollectible").length},
              {val:"void",           label:"Void",          count:invoices.filter(i=>i.status==="void").length},
            ].map(({val,label,count})=>(
              <div key={val} className={`tab${filter===val?" active":""}`} onClick={()=>setFilter(val)}>
                {label}
                {count>0&&<span style={{marginLeft:5,background:val==="uncollectible"?"#ffebe6":val==="pending"?"#fffae6":val==="needs_approval"?"#fffae6":"#f4f5f7",color:val==="uncollectible"?"#bf2600":val==="pending"?"#974f0c":val==="needs_approval"?"#974f0c":"#42526e",fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10}}>{count}</span>}
              </div>
            ))}
          </div>
          <span style={{fontFamily:"Syne",fontWeight:700,fontSize:13,color:NAVY}}>
            {search ? `${filtered.length} result${filtered.length!==1?"s":""} · $${total.toFixed(2)}` : `Showing: $${total.toFixed(2)}`}
          </span>
        </div>
        {ACTIVE_THEME === "apple-native" ? (
          <div>
            {filtered.length===0 && <div className="empty"><p>No invoices in this category</p></div>}
            {filtered.map(inv=>{
              const hov = hoveredId===inv.id;
              // Touch has no hover: on phones the action bar is always open (mouse handlers
              // dropped so iOS's hover emulation can't leave rows randomly stuck expanded).
              const show = isMobile || hov;
              const failed = inv.status==="open" && Number(inv.attempt_count||0)>0 && Math.round(Number(inv.amount_paid||0)*100) < Math.round(Number(inv.amount||0)*100);
              const color = inv.status==="paid"?"green":inv.status==="partially_refunded"?"gold":inv.status==="open"?"gold":inv.status==="draft"?"purple":"gray";
              const label = inv.status==="void"?"Void":inv.status==="uncollectible"?"Uncollectible":inv.status==="partially_refunded"?"Partially refunded":inv.status;
              return (
                <div key={inv.id}
                  onMouseEnter={isMobile?undefined:()=>setHoveredId(inv.id)} onMouseLeave={isMobile?undefined:()=>setHoveredId(null)}
                  onClick={(e)=>{ if(e.target.closest("button")) return; setInvoiceModal(inv); }}
                  style={{padding:isMobile?"14px 4px":"14px 12px",borderRadius:12,cursor:"pointer",background:hov?"var(--accent-weak)":"transparent",transition:"background .15s ease",borderBottom:hov?"1px solid transparent":"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                        <span style={{fontWeight:600,fontSize:14,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inv.client_name}</span>
                        {failed
                          ? <span className="badge" style={{background:"#ffebe6",color:"#bf2600",fontWeight:600,flexShrink:0}}>Payment failed</span>
                          : <span className={`badge badge-${color}`} style={{textTransform:"capitalize",flexShrink:0}}>{label}</span>}
                      </div>
                      <div style={isMobile
                        ? {fontSize:12,color:"var(--text2)",marginTop:3,lineHeight:1.4}
                        : {fontSize:12,color:"var(--text2)",marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {[inv.invoice_number, inv.date, inv.due_date&&`due ${inv.due_date}`, inv.frequency&&(inv.frequency[0].toUpperCase()+inv.frequency.slice(1)), inv.dogs&&`${inv.dogs} dog${inv.dogs>1?"s":""}`].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div style={{fontWeight:600,fontSize:16,letterSpacing:"-.02em",color:"var(--text)",flexShrink:0,whiteSpace:"nowrap"}}>${fmtAmt(inv.amount)}</div>
                  </div>
                  <div style={isMobile
                    ? {display:"flex",gap:8,flexWrap:"wrap",marginTop:12}
                    : {display:"flex",gap:6,flexWrap:"wrap",overflow:"hidden",maxHeight:show?60:0,opacity:show?1:0,marginTop:show?12:0,transition:"max-height .2s ease,opacity .2s ease,margin .2s ease"}}>
                    {inv.status!=="paid"&&inv.status!=="partially_refunded"&&<button className="btn btn-ghost btn-sm" style={actBtn()} onClick={()=>setEditInvoice(inv)}>✏️ Edit</button>}
                    <button className="btn btn-ghost btn-sm" style={actBtn()} onClick={()=>setInvoiceModal(inv)}>📄 Preview</button>
                    {inv.status==="draft"&&<button className="btn btn-gold btn-sm" style={actBtn(isMobile?{flex:"1 1 100%"}:undefined)} onClick={()=>onApprove&&onApprove(inv)}>⚡ {inv.stripe_invoice_id ? "Finalize & Charge" : "Approve & Charge"}</button>}
                    {inv.status==="open"&&inv.stripe_invoice_id&&<button className="btn btn-gold btn-sm" style={actBtn(isMobile?{flex:"1 1 100%"}:undefined)} onClick={()=>onApprove&&onApprove(inv)}>⚡ Charge Now</button>}
                    {inv.status!=="void"&&inv.status!=="paid"&&inv.status!=="refunded"&&inv.status!=="partially_refunded"&&<button className="btn btn-sm" style={actBtn({background:"#f4f5f7",color:"#42526e",border:"none"})} onClick={()=>onVoidInvoice&&onVoidInvoice(inv)}>⊘ Void</button>}
                    {inv.status==="uncollectible"&&<button className="btn btn-sm" style={actBtn({background:"#ffebe6",color:"#bf2600",border:"none"})} onClick={()=>onRetryCharge&&onRetryCharge(inv)}>🔄 Retry</button>}
                    {(inv.status==="paid"||inv.status==="partially_refunded")&&<button className="btn btn-sm" style={actBtn({background:"#ffebe6",color:"#bf2600",border:"none"})} onClick={()=>onIssueRefund&&onIssueRefund(inv)}>↩ Refund</button>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Invoice #</th><th>Client</th><th>Frequency</th><th>Dogs</th><th>Amount</th><th>Date</th><th>Due</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(inv=>(
                <tr key={inv.id} style={{cursor:"pointer"}} onClick={(e)=>{ if(e.target.tagName==="BUTTON"||e.target.closest("button")) return; setInvoiceModal(inv); }}>
                  <td style={{fontFamily:"Syne",fontWeight:700,fontSize:12}}>{inv.invoice_number}</td>
                  <td style={{fontWeight:600}}>{inv.client_name}</td>
                  <td style={{textTransform:"capitalize",fontSize:12}}>{inv.frequency}</td>
                  <td>{inv.dogs}🐕</td>
                  <td style={{fontFamily:"Syne",fontWeight:800,color:NAVY}}>${fmtAmt(inv.amount)}</td>
                  <td style={{fontSize:12}}>{inv.date}</td>
                  <td style={{fontSize:12}}>{inv.due_date}</td>
                  <td>{(() => {
                    // Mirror Stripe's "Failed" badge: invoice.status stays "open" after a
                    // failed charge attempt, so derive it from attempt_count + unpaid amount.
                    const failed = inv.status==="open" && Number(inv.attempt_count||0) > 0
                      && Math.round(Number(inv.amount_paid||0)*100) < Math.round(Number(inv.amount||0)*100);
                    if (failed) return <span className="badge" style={{background:"#ffebe6",color:"#bf2600",fontWeight:600}} title={`Stripe attempted to charge and it failed (attempt ${inv.attempt_count}). Update the card or retry.`}>Payment failed</span>;
                    const color = inv.status==="paid"?"green":inv.status==="partially_refunded"?"gold":inv.status==="open"?"gold":inv.status==="draft"?"purple":"gray";
                    const label = inv.status==="void"?"Void":inv.status==="uncollectible"?"Uncollectible":inv.status==="partially_refunded"?"Partially refunded":inv.status;
                    return <span className={`badge badge-${color}`} style={{textTransform:"capitalize"}}>{label}</span>;
                  })()}</td>
                  <td>
                    <div className="flex gap-2 row-actions">
                      {inv.status!=="paid"&&inv.status!=="partially_refunded"&&<button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setEditInvoice(inv)}>✏️ Edit</button>}
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setInvoiceModal(inv)}>📄 Preview</button>
                      {inv.status==="draft"&&<button className="btn btn-gold btn-sm" style={{fontSize:11}} onClick={()=>onApprove&&onApprove(inv)}>⚡ {inv.stripe_invoice_id ? "Finalize & Charge" : "Approve & Charge"}</button>}
                      {inv.status==="open"&&inv.stripe_invoice_id&&<button className="btn btn-gold btn-sm" style={{fontSize:11}} onClick={()=>onApprove&&onApprove(inv)}>⚡ Charge Now</button>}
                      {inv.status!=="void"&&inv.status!=="paid"&&inv.status!=="refunded"&&inv.status!=="partially_refunded"&&<button className="btn btn-sm" style={{background:"#f4f5f7",color:"#42526e",border:"none",fontSize:11}} onClick={()=>onVoidInvoice&&onVoidInvoice(inv)}>⊘ Void</button>}
                      {inv.status==="uncollectible"&&<button className="btn btn-sm" style={{background:"#ffebe6",color:"#bf2600",border:"none",fontSize:11}} onClick={()=>onRetryCharge&&onRetryCharge(inv)}>🔄 Retry</button>}
                      {(inv.status==="paid"||inv.status==="partially_refunded")&&<button className="btn btn-sm" style={{background:"#ffebe6",color:"#bf2600",border:"none",fontSize:11}} onClick={()=>onIssueRefund&&onIssueRefund(inv)}>↩ Refund</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length===0&&<tr><td colSpan={9}><div className="empty"><p>No invoices in this category</p></div></td></tr>}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* ── Edit Invoice Modal ── */}
      {editInvoice && (
        <InvoiceEditModal
          invoice={editInvoice}
          onSave={(updated)=>{ onEdit(updated); setEditInvoice(null); }}
          onSaveAndPreview={(updated)=>{ onEdit(updated); setEditInvoice(null); setInvoiceModal(updated); }}
          onClose={()=>setEditInvoice(null)}
          onApprove={(inv)=>{ onApprove(inv); setEditInvoice(null); }}
        />
      )}

      {/* ── Invoice Preview & Send Modal ── */}
      {invoiceModal && (
        <InvoicePreviewModal
          invoice={invoiceModal}
          onClose={()=>setInvoiceModal(null)}
        />
      )}
    </div>
  );
}
